/**
 * Browser Meta Pixel integration.
 *
 * Meta receives acquisition conversions only. Product usage, learning
 * activity, school context, and authenticated dashboard page views stay in
 * first-party/product analytics and are deliberately excluded here.
 */
import { analytics } from './analytics.js';
import { CHECKOUT_STAGES, EVENTS, PROPS } from './events.js';

const SCRIPT_ID = 'meta-pixel-script';

export const META_PAGEVIEW_ROUTES = Object.freeze([
  'home-lp',
  'teacher-landing',
  'partners-lp',
  'blogs',
  'blog-detail',
  'terms-of-service',
  'privacy-policy',
  'cookie-policy',
  'login',
  'auth-register',
  'register-organisation',
  'subscribe-standalone',
  'payment-success',
]);

let pixelInitialised = false;
let stopEventSubscription = null;
let pageviewsBound = false;

function installPixelQueue() {
  if (typeof window === 'undefined') return null;
  if (window.fbq) return window.fbq;

  const fbq = function (...args) {
    if (fbq.callMethod) fbq.callMethod(...args);
    else fbq.queue.push(args);
  };
  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = '2.0';
  fbq.queue = [];

  window.fbq = fbq;
  window._fbq = fbq;
  return fbq;
}

function loadPixelScript() {
  if (typeof document === 'undefined' || document.getElementById(SCRIPT_ID)) {
    return;
  }

  const script = document.createElement('script');
  script.id = SCRIPT_ID;
  script.async = true;
  script.src = 'https://connect.facebook.net/en_US/fbevents.js';
  document.head.appendChild(script);
}

export function shouldTrackMetaPageview(routeName) {
  return META_PAGEVIEW_ROUTES.includes(routeName);
}

function trackPageview(route) {
  if (!shouldTrackMetaPageview(route?.name)) return;
  window.fbq?.('track', 'PageView');
}

function bindPageviews(router) {
  if (pageviewsBound) return;
  pageviewsBound = true;
  router.afterEach((to) => trackPageview(to));
}

/**
 * Convert the app's canonical event into a Meta standard event. Exported as a
 * pure function so conversion semantics can be regression tested.
 */
export function metaEventForAnalyticsEvent(event, properties = {}) {
  if (event === EVENTS.SIGNUP_COMPLETED) {
    return { name: 'CompleteRegistration', parameters: {} };
  }

  if (event === EVENTS.TRIAL_STARTED) {
    return { name: 'StartTrial', parameters: {} };
  }

  if (
    event === EVENTS.CHECKOUT_STEP_VIEWED &&
    properties[PROPS.FUNNEL_STAGE] === CHECKOUT_STAGES.ENTERED
  ) {
    return { name: 'InitiateCheckout', parameters: {} };
  }

  if (event === EVENTS.SUBSCRIPTION_PURCHASED) {
    const value = Number(properties[PROPS.AMOUNT]);
    const currency = properties[PROPS.CURRENCY];
    if (!Number.isFinite(value) || value < 0 || !currency) return null;

    return {
      name: 'Purchase',
      parameters: {
        value,
        currency: String(currency).toUpperCase(),
      },
    };
  }

  return null;
}

function trackAnalyticsEvent(event, properties) {
  const metaEvent = metaEventForAnalyticsEvent(event, properties);
  if (!metaEvent) return;

  window.fbq?.('track', metaEvent.name, metaEvent.parameters);
}

/** Initialise Meta when the production Pixel ID is configured. */
export function initMetaPixel(router) {
  const pixelId = import.meta.env.VITE_META_PIXEL_ID;
  if (!pixelId || typeof window === 'undefined') return false;

  const fbq = installPixelQueue();
  if (!fbq) return false;

  if (!pixelInitialised) {
    loadPixelScript();
    fbq('init', pixelId);
    // Events Manager can enable automatic button/form inference remotely.
    // Keep this integration explicit so learning activity and form fields
    // cannot silently become new Meta events without a code review.
    fbq('set', 'autoConfig', false, pixelId);
    pixelInitialised = true;
  }

  bindPageviews(router);

  if (!stopEventSubscription) {
    stopEventSubscription = analytics.subscribe(trackAnalyticsEvent);
  }

  // Usually called before the router's first navigation, which afterEach
  // catches. This also covers callers that initialise after navigation.
  trackPageview(router.currentRoute?.value);
  return true;
}
